import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ConditionalElement } from "../../../../../rule-engine-core/TextMerge/Elements/ConditionalElement";
import { FieldElement } from "../../../../../rule-engine-core/TextMerge/Elements/Text/FieldElement";
import { TextElement } from "../../../../../rule-engine-core/TextMerge/Elements/Text/TextElement";
import { TextMergeConfiguration } from "../../../../../rule-engine-core/TextMerge/TextMergeConfiguration";
import { TextMergeType } from "../../../../../rule-engine-core/TextMerge/TextMergeType";
import { renderRuleRoutingActionLines } from "../../../domain/ruleRouting";
import { createRuleSummaryPresentation, type RuleIntegrationEvaluationContext } from "../../ruleIntegrationTypes";
import {
    runTextMergeToPlainText,
    type CreateTextMergeIntegrationAdapterParams,
} from "../../executableFamilies/textMerge/TextMergeIntegrationAdapter";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import type { DemoData, EnrichedPart } from "../../../domain/types";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import { getOutcomeRouting, type RuleOutcomeKey, type RuleOutcomeRouting } from "../../../rules/ruleLogicDefinitions";
import {
    ERP_DESCRIPTION_LIMIT,
    compactMpn,
    formatCapacitance,
    formatResistance,
    formatTolerance,
    formatVoltage,
} from "../../../outputs/buildDownstreamPayloads";

export const TR006_RULE_ID = "TR-006";
export const TR006_APPGLUE_EXECUTABLE_TYPE = "TextMergeConfiguration";
export const TR006_OUTPUT_QUEUE: DemoOutputQueue = "epicor_description_previews";

export type TextMergePartDemoRow = {
    data: DemoData;
    part: EnrichedPart;
};

export type Tr006TextMergeEvidence = {
    status: DemoRuleStatus;
    textMergeOutput: string;
    erpDescription: string;
    erpDescriptionLimit: number;
    wasTruncated: boolean;
    droppedFragments: string[];
    outputQueue?: DemoOutputQueue;
};

const TEXT_FIELD_NAMES = [
    "manufacturer_name",
    "mpn",
    "category",
    "voltage_range",
    "package_type",
    "pin_count",
    "capacitance",
    "resistance",
    "tolerance",
    "rohs_status",
    "lifecycle_status",
] as const;

const BOOLEAN_FIELD_NAMES = [
    "has_category",
    "has_voltage_range",
    "has_package_type",
    "has_pin_count",
    "has_capacitance",
    "has_resistance",
    "has_tolerance",
    "has_rohs_status",
    "has_lifecycle_status",
] as const;

type TextMergePartFieldName = (typeof TEXT_FIELD_NAMES)[number];
type TextMergePartConditionName = (typeof BOOLEAN_FIELD_NAMES)[number];

const DEFAULT_TR006_OUTCOME_ROUTING: Record<RuleOutcomeKey, RuleOutcomeRouting> = {
    pass: {
        id: "tr006-pass-routing",
        when: "pass",
        status: "pass",
        label: "publish ERP description preview",
        outputQueue: TR006_OUTPUT_QUEUE,
        reasonCode: "DESCRIPTION_READY",
        explanation:
            "ERP description preview was built from the saved AppGlue text merge and fit to the downstream field limit.",
    },
    fail: {
        id: "tr006-fail-routing",
        when: "fail",
        status: "flagged",
        label: "hold incomplete description inputs",
        outputQueue: "pending_review",
        reasonCode: "DESCRIPTION_INPUT_INCOMPLETE",
        explanation: "Description formatting is held because priority product attributes are missing.",
    },
    error: {
        id: "tr006-error-routing",
        when: "error",
        status: "rejected",
        label: "hold text merge runtime error",
        outputQueue: "pending_review",
        reasonCode: "TEXT_MERGE_RUNTIME_ERROR",
        explanation: "The AppGlue text merge could not produce a usable description preview.",
    },
};

const getTr006OutcomeRouting = (
    context: RuleIntegrationEvaluationContext | undefined,
    when: RuleOutcomeKey,
): RuleOutcomeRouting => getOutcomeRouting(context?.definition, when) ?? DEFAULT_TR006_OUTCOME_ROUTING[when];

const createTextMergePartInputDefinition = (): DataDefinition => {
    const inputData = new DataDefinition();
    TEXT_FIELD_NAMES.forEach((fieldName) => inputData.fields.push(new StringDataDefinition(fieldName)));
    BOOLEAN_FIELD_NAMES.forEach((fieldName) => inputData.fields.push(new BooleanDataDefinition(fieldName)));
    return inputData;
};

const textField = (label: string, variableName: TextMergePartFieldName): FieldElement => {
    const element = new FieldElement(label);
    element.fieldData.variableName = variableName;
    return element;
};

const addLiteral = (config: TextMergeConfiguration, text: string) => {
    config.rootElement.addElement(new TextElement(text));
};

const addField = (config: TextMergeConfiguration, label: string, variableName: TextMergePartFieldName) => {
    config.rootElement.addElement(textField(label, variableName));
};

const addConditionalFragment = (
    config: TextMergeConfiguration,
    conditionField: TextMergePartConditionName,
    label: string,
    variableName: TextMergePartFieldName,
) => {
    const conditional = new ConditionalElement();
    conditional.condition.variableName = conditionField;
    conditional.addElement(new TextElement("|"));
    conditional.addElement(textField(label, variableName));
    config.rootElement.addElement(conditional);
};

export const createTr006ErpDescriptionTextMergeConfiguration = (): TextMergeConfiguration => {
    AppGlueCoreInit.init();

    const config = new TextMergeConfiguration();
    config.type = TextMergeType.PlAIN;
    config.defaultValue = "";
    config.inputData = createTextMergePartInputDefinition();
    config.executionResult = new StringDataDefinition();

    addField(config, "Manufacturer", "manufacturer_name");
    addLiteral(config, "|");
    addField(config, "MPN", "mpn");
    addConditionalFragment(config, "has_category", "Category", "category");
    addConditionalFragment(config, "has_voltage_range", "Voltage range", "voltage_range");
    addConditionalFragment(config, "has_package_type", "Package", "package_type");
    addConditionalFragment(config, "has_pin_count", "Pin count", "pin_count");
    addConditionalFragment(config, "has_capacitance", "Capacitance", "capacitance");
    addConditionalFragment(config, "has_resistance", "Resistance", "resistance");
    addConditionalFragment(config, "has_tolerance", "Tolerance", "tolerance");
    addConditionalFragment(config, "has_rohs_status", "RoHS", "rohs_status");
    addConditionalFragment(config, "has_lifecycle_status", "Lifecycle", "lifecycle_status");

    return config;
};

const present = (value: string | null | undefined): value is string => typeof value === "string" && value.length > 0;

const buildTextMergePartFields = (part: EnrichedPart): Record<string, string | boolean> => {
    const voltageRange = formatVoltage(part);
    const capacitance = formatCapacitance(part.capacitancePf);
    const resistance = formatResistance(part.resistanceOhms);
    const tolerance = formatTolerance(part.tolerancePct);
    const packageType = part.packageType ?? "";
    const pinCount = part.pinCount == null ? "" : `${part.pinCount} pins`;
    const rohsStatus = part.rohsStatus == null ? "" : `RoHS ${part.rohsStatus}`;
    const lifecycleStatus = `Lifecycle ${part.lifecycleStatus}`;

    return {
        manufacturer_name: part.manufacturerName,
        mpn: compactMpn(part.mpn),
        category: part.category,
        voltage_range: voltageRange ?? "",
        package_type: packageType,
        pin_count: pinCount,
        capacitance: capacitance ?? "",
        resistance: resistance ?? "",
        tolerance: tolerance ?? "",
        rohs_status: rohsStatus,
        lifecycle_status: lifecycleStatus,
        has_category: present(part.category),
        has_voltage_range: present(voltageRange),
        has_package_type: present(packageType),
        has_pin_count: present(pinCount),
        has_capacitance: present(capacitance),
        has_resistance: present(resistance),
        has_tolerance: present(tolerance),
        has_rohs_status: present(rohsStatus),
        has_lifecycle_status: present(lifecycleStatus),
    };
};

export const buildTr006ExecutionData = (part: EnrichedPart): ExecutionData =>
    new ExecutionData(buildTextMergePartFields(part));

const fitDescriptionFragments = (baseFragments: string[], optionalFragments: string[], limit: number) => {
    const used = [...baseFragments];
    const dropped: string[] = [];

    optionalFragments.forEach((fragment) => {
        const candidate = [...used, fragment].join(" | ");
        if (candidate.length <= limit) {
            used.push(fragment);
        } else {
            dropped.push(fragment);
        }
    });

    const description = used.join(" | ");
    if (description.length <= limit) {
        return { description, dropped };
    }

    return {
        description: `${description.slice(0, Math.max(0, limit - 3))}...`,
        dropped,
    };
};

export const fitErpDescriptionTextMergeOutput = (textMergeOutput: string, limit = ERP_DESCRIPTION_LIMIT) => {
    const fragments = textMergeOutput
        .split(/\s*\|\s*/)
        .map((fragment) => fragment.trim())
        .filter(Boolean);

    if (fragments.length >= 2) {
        const { description, dropped } = fitDescriptionFragments(fragments.slice(0, 2), fragments.slice(2), limit);
        return {
            description,
            droppedFragments: dropped,
            wasTruncated: description !== textMergeOutput,
        };
    }

    if (textMergeOutput.length <= limit) {
        return {
            description: textMergeOutput,
            droppedFragments: [],
            wasTruncated: false,
        };
    }

    return {
        description: `${textMergeOutput.slice(0, Math.max(0, limit - 3))}...`,
        droppedFragments: [],
        wasTruncated: true,
    };
};

const evaluateTr006Description = (
    config: TextMergeConfiguration,
    part: EnrichedPart,
    context?: RuleIntegrationEvaluationContext,
): Tr006TextMergeEvidence => {
    const textMergeOutput = runTextMergeToPlainText(config, buildTr006ExecutionData(part));
    const description = fitErpDescriptionTextMergeOutput(textMergeOutput);
    const routing = getTr006OutcomeRouting(context, part.hasMandatoryAttributeGap ? "fail" : "pass");

    return {
        status: routing.status,
        textMergeOutput,
        erpDescription: description.description,
        erpDescriptionLimit: ERP_DESCRIPTION_LIMIT,
        wasTruncated: description.wasTruncated,
        droppedFragments: description.droppedFragments,
        outputQueue: routing.outputQueue,
    };
};

const renderConfiguredFields = (config: TextMergeConfiguration): string => {
    const fields = config.rootElement.elements
        .flatMap((element) => ("elements" in element ? element.elements : [element]))
        .filter((element): element is FieldElement => element instanceof FieldElement)
        .map((element) => element.fieldName);

    return fields.length > 0 ? fields.join(", ") : "fields stored in TextMergeConfiguration";
};

const renderTr006Summary = (config: TextMergeConfiguration, context?: RuleIntegrationEvaluationContext) =>
    createRuleSummaryPresentation({
        status: "complete",
        lines: [
            `TEXT MERGE ${renderConfiguredFields(config)}`,
            `LIMIT ${ERP_DESCRIPTION_LIMIT} ERP description characters`,
            ...renderRuleRoutingActionLines(context?.definition?.outcomeRouting ?? Object.values(DEFAULT_TR006_OUTCOME_ROUTING)),
        ],
        sources: ["native_appglue_config", "adapter_projection"],
    });

const samplePart = (overrides: Partial<EnrichedPart> = {}): EnrichedPart => ({
    partId: "PART-TR006-SAMPLE",
    mpn: "TI-TR006",
    manufacturerId: "MFR-001",
    manufacturerName: "Texas Instruments",
    category: "Semiconductor",
    taxonomyPath: "Semiconductor > Microcontroller > 32-bit",
    packageType: "QFN",
    pinCount: 32,
    voltageMin: 1.8,
    voltageMax: 3.3,
    capacitancePf: null,
    resistanceOhms: null,
    tolerancePct: null,
    rohsStatus: "Compliant",
    reachStatus: "Compliant",
    lifecycleStatus: "Active",
    priorLifecycleStatus: "Active",
    confidenceScore: 96,
    source: "manufacturer_direct",
    recommendedReplacementMpn: null,
    hasMandatoryAttributeGap: false,
    hasUomConflict: false,
    hasMpnFormatIssue: false,
    hasComplianceChange: false,
    hasSourceConflict: false,
    ...overrides,
});

const sampleData = (part: EnrichedPart): DemoData =>
    (({
        seed: 20260501,
        manufacturers: [],
        parts: [part],
        catalog: [],
        priceUpdates: [],
        pricingHistory: [],
        priceToleranceRules: [],
        taxonomyMappings: [
            {
                taxonomyPath: part.taxonomyPath,
                erpProductGroup: "SEM-MCU",
                erpProductLine: "Microcontrollers",
                crmFamily: "Semiconductors",
            },
        ],
        sourcePriorityRules: [],
        opportunities: [],
        purchaseOrders: [],
        summary: {},
    }) as unknown) as DemoData;

export const TR006_TEXT_MERGE_POLICY: CreateTextMergeIntegrationAdapterParams<TextMergePartDemoRow> = {
    ruleId: TR006_RULE_ID,
    policyBinding: requireRulePolicyBinding(TR006_RULE_ID),
    createConfig: createTr006ErpDescriptionTextMergeConfiguration,
    evaluator: {
        evaluate: (config, row, context) => {
            const evidence = evaluateTr006Description(config, row.part, context);

            return {
                passed: evidence.status === "pass",
                status: evidence.status,
                reasonCode: getTr006OutcomeRouting(context, evidence.status === "pass" ? "pass" : "fail").reasonCode,
                explanation: getTr006OutcomeRouting(context, evidence.status === "pass" ? "pass" : "fail").explanation,
                outputQueue: evidence.outputQueue,
                comparisons: [
                    {
                        label: "Text merge output",
                        expected: "saved TextMergeConfiguration",
                        actual: evidence.textMergeOutput || "empty",
                        ok: evidence.textMergeOutput.length > 0,
                    },
                    {
                        label: "ERP description length",
                        expected: `${evidence.erpDescriptionLimit} chars or fewer`,
                        actual: `${evidence.erpDescription.length} chars`,
                        ok: evidence.erpDescription.length <= evidence.erpDescriptionLimit,
                    },
                ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: TR006_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence,
            };
        },
    },
    summaryPresenter: {
        renderSummary: renderTr006Summary,
    },
};

const passingPart = samplePart();
const routedPart = samplePart({
    partId: "PART-TR006-ROUTED",
    packageType: null,
    hasMandatoryAttributeGap: true,
});

export const TR006_RUNTIME_SAMPLES = {
    passingRow: { data: sampleData(passingPart), part: passingPart },
    routedRow: { data: sampleData(routedPart), part: routedPart },
};
